import { createContext, useCallback, useMemo, useState, type ReactNode } from 'react'
import noop from 'lib/function/noop'

interface SelectionQuote {
  maximum?: number
  fetching: boolean
  failed?: boolean
  ready: boolean
}

interface ContextValue {
  quote?: SelectionQuote
  setQuote: (quote: SelectionQuote | undefined, ownerId?: string) => void
}

export const OfferPageBurnSelectionContext = createContext<ContextValue>({ setQuote: noop })

/** Scoped to the mounted offer page so previous selections cannot set its slider maximum. */
export function OfferPageBurnSelectionProvider({ children }: { children: ReactNode }) {
  const [quotes, setQuotes] = useState(() => new Map<string, SelectionQuote>())
  const setQuote = useCallback((quote: SelectionQuote | undefined, ownerId = 'default') => {
    setQuotes(current => {
      if (current.get(ownerId) === quote) return current
      const next = new Map(current)
      next.delete(ownerId)
      if (quote) next.set(ownerId, quote)
      return next
    })
  }, [])
  // The latest registration wins; removing it restores a surviving page/modal owner.
  const quote = useMemo(() => Array.from(quotes.values()).pop(), [quotes])
  const value = useMemo(() => ({ quote, setQuote }), [quote, setQuote])
  return <OfferPageBurnSelectionContext.Provider value={value}>{children}</OfferPageBurnSelectionContext.Provider>
}
